import React, {useState, useEffect, useContext, useCallback} from 'react'
import debounce from "lodash/debounce";
import { ContentWrapper, Header, RateBox } from '../style'
import CustomSelect from '../../../../components/common/CustomSelect';
import InputAndSelect from '../../../../components/common/InputAndSelect';
import { Button } from '../../../../components/common/Button';
import { getDefaultRate, getExchangeRate, getPayingCurrencies, getReceivingCurrenciesByCountry, getWireChargesV3 } from '../../wiresAPI';
import ErrorMessage from '../../../../components/ErrorMessage';
import { NetworkErrorAlertContext } from '../../../../context/NetworkErrorAlert';
import { formatCurrencyAmount, formatNewKwireAmount } from '../../../../utils/currency';
import { PaymentProps } from '../types';
import { useAppSelector } from '../../../../app/hooks';
import {useNavigate} from "react-router-dom";
import {SUPPORTED_PAWA_PAY_COUNTRIES} from "../../../Wallets/const";
import { countryOptions } from './Beneficiary/BeneficiaryModal/utils';
import { appRoutes } from '../../../../app/router/utils';

const PaymentDetails = ({
    setStep,
    setBuyingRate,
    countries,
    selectedCountry,
    setSelectedCountry,
    selectedCountryCode,
    setSelectedCountryCode,
    setLoading,
    amount,
    setAmount,
    payAmount,
    setPayAmount,
    setRate,
    setCharges,
    setDuration,
    totalAmount,
    setTotalAmount,
    selectedRecCurr,
    setSelectedRecCurr,
    selectedPayCurr,
    setSelectedPayCurr,
    payingCurrencies,
    setPayingCurrencies,
    receivingCurrencies,
    setReceivingCurrencies,
}: PaymentProps) => {
    const minimum_usd = process.env.REACT_APP_ENV === "dev" ? 1 : 100;

    const [minAmount, setMinAmount] = useState(minimum_usd);
    const [error, setError] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [convertedRate, setConvertedRate] = useState<string>(null);
    const [totalFee, setTotalFee] = useState<number>(null);
    const { onShowAlert } = useContext(NetworkErrorAlertContext);
    const { businessId } = useAppSelector((state) => state.users) || {};
    const { wallets } = useAppSelector((state) => state?.wallets);
    const navigate = useNavigate()

    const hasUsdWallet = wallets?.some((wallet) => wallet.currency === "USD")

    const onSendCountryChange = (value: string) => {
        if (value === "China") {
            return navigate(appRoutes.cnyPayoutv2());
        }

        const selectedCountry = SUPPORTED_PAWA_PAY_COUNTRIES.find(
            (country) => country.value === value
        );

        if (selectedCountry) {
            if (!hasUsdWallet) {
                onShowAlert("error", `You need to have a USD wallet to send wire to this country. Create one on Wallets page.`);
                setTimeout(() => {
                    navigate('/wallets')
                }, 2000);
                return
            }
            return navigate(`/wallets/${selectedCountry.currencyCode}/${selectedCountry.slug}`);
        }

        const matchedCountry = countryOptions.find((c) => c.label === value);
        const countryCode = matchedCountry?.code || value;

        setSelectedCountry(value);
        setSelectedCountryCode(countryCode);
        setSelectedRecCurr(null);
        setSelectedPayCurr(null);
        setPayingCurrencies([])
        setAmount('')
        setTotalAmount('')
        setConvertedRate(null)
    };

    const getAmountAndCharges = async (recCurr: string, amt: string, payingCurr: string) =>{
        if(recCurr && amt && payingCurr && selectedCountryCode){
            try{
                const intAmount: number = +amt;
                setLoading(true);

                let rateAmt = intAmount;

                const data = await getExchangeRate(payingCurr, intAmount, recCurr, businessId, "KLASHA_WIRE")
                const rate = data?.rate;
                const highestAmt = (rate && rate !== 0) ? intAmount * (1 / rate) : 0;
              
                if(recCurr !== "USD" && payingCurr !== "USD"){
                    const rateData = await getExchangeRate(recCurr, intAmount, "USD", businessId, "KLASHA_WIRE");
                    rateAmt = rateData?.amount;
                } else {
                    rateAmt = highestAmt;
                }
                
                const feeData = await getWireChargesV3(payingCurr, recCurr, selectedCountryCode, rateAmt, businessId);

                const calculatedFee = feeData?.fee ?? 0;
                const totalAmt = highestAmt + calculatedFee;

                setTotalFee(calculatedFee)
                setRate(1 / rate);
                setBuyingRate(1 / data?.buyingRate)
                setCharges(calculatedFee)
                setPayAmount(highestAmt.toFixed(2));
                setConvertedRate(`${rate} (${(1 / (rate)).toFixed(6)})`);
                setTotalAmount(totalAmt.toFixed(2))
                setDuration(feeData?.duration || "1 - 4 Business days")

                setLoading(false)
                setIsTyping(false);
            } catch (err) {
                setLoading(false)
                onShowAlert(err, err.message);
                setIsTyping(false);
            }
        }
    }

    const receivingCurrencyChange = async (value: string) =>{
        setTotalAmount('')
        setRate(null)
        setSelectedPayCurr(null);
        setSelectedRecCurr(value);
        setConvertedRate(null)
        setMinAmount(minimum_usd);

        if (value !== "USD") {
            try {
                const ExcData = await getDefaultRate("USD", minimum_usd, value);
                const excRate = ExcData.rate || 0;

                setMinAmount(Math.ceil(excRate));
            } catch (e) {
                setMinAmount(minimum_usd);
            }
        }
    }

    const payingCurrencyChange = async (amt: string, payingCurr: string) =>{
       if(amt){
            setSelectedPayCurr(payingCurr);
       } else {
            onShowAlert(Error, "Please enter the amount your beneficiary should receive");
       }
    }

    const getReceivingCurrencies = async (value: string) => {
        try {
            const data = await getReceivingCurrenciesByCountry(value)
            const options = data.map((c) => ({
                label: c.availableCurrency,
                value: c.availableCurrency,
              }));

            setReceivingCurrencies(options)
        } catch (err) {
            onShowAlert(err, "Failed to get currencies");
        }
    }

    const getPayingCurrency = async (value: string) => {
        try {
            const data = await getPayingCurrencies(value)
            const options = data.map((c) => ({
                label: c.sourceCurrency,
                value: c.sourceCurrency,
              }));

            setPayingCurrencies(options)
        } catch (err) {
            onShowAlert(err, err.message);
        }
    }

    useEffect(() => {
        if(selectedCountry){
            getReceivingCurrencies(selectedCountry)
        }
    }, [selectedCountry]);

    useEffect(() => {
        if(selectedRecCurr){
            getPayingCurrency(selectedRecCurr)
        }
    }, [selectedRecCurr]);

    const limitMultiplierValue = selectedRecCurr === "USDT" ? 500000 : 5000

    useEffect(() => {
        if (selectedRecCurr !== "GBP") {
            if (amount && parseFloat(amount) < minAmount) {
                setError(true);
                setErrorMsg(`Minimum amount: ${selectedRecCurr} ${new Intl.NumberFormat().format(minAmount)}`);
            } else if (amount && parseFloat(amount) > minAmount * limitMultiplierValue) {
                setError(true);
                setErrorMsg(`Maximum amount: ${selectedRecCurr} ${new Intl.NumberFormat().format(minAmount * limitMultiplierValue)}`);
            } else {
                setError(false);
            }
        } else {
            setError(false);
        }
    }, [amount, minAmount, selectedRecCurr])

    const debouncedSearch = useCallback(
        debounce(() => {
            getAmountAndCharges(selectedRecCurr, amount, selectedPayCurr)
        }, 1000),
        [selectedRecCurr, amount, selectedPayCurr, selectedCountryCode]
    );

    useEffect(() => {
        setIsTyping(true);
        debouncedSearch();

        return debouncedSearch.cancel;
      }, [selectedRecCurr, amount, selectedPayCurr, selectedCountryCode, debouncedSearch]);

  return (
    <ContentWrapper>
       <Header>
        <h1>How much do you want to send?</h1>
       </Header>

       <CustomSelect
            placeholder="Beneficiary country"
            options={countries}
            value={selectedCountry}
            onChange={onSendCountryChange}
            $width="100%"
            showSearch
            lightBorder
        />

        <div>
            <InputAndSelect
                amountValue={amount}
                selectPlaceholder='Currency'
                onAmountChange={(value: string) =>  setAmount(value)}
                width='100%'
                options={receivingCurrencies}
                selectValue={selectedRecCurr}
                onSelectChange={(value: string) =>receivingCurrencyChange(value)}
            />
            {error && <ErrorMessage message={errorMsg}/>}
        </div>

        {convertedRate && 
            <RateBox>
                <span>{convertedRate ? formatNewKwireAmount(selectedPayCurr, convertedRate): ''}</span>
                <span>Exchange rate</span>
            </RateBox>
        }

        {totalFee >= 0 && totalFee !== null && 
           ( <RateBox>
                <span>{totalAmount ? formatCurrencyAmount(selectedPayCurr, totalFee): ''}</span>
                <span>Processing fee</span>
            </RateBox>)
        }
        <div>
            <InputAndSelect
                amountValue={payAmount}
                onAmountChange={(value: string) => setPayAmount(value)}
                readOnly={true}
                width='100%'
                placeholder='Total amount to be charged'
                selectPlaceholder='Currency'
                options={payingCurrencies}
                selectValue={selectedPayCurr}
                onSelectChange={(value: string) => payingCurrencyChange(amount, value)}
            />
        </div>

        <Button
            label='Next'
            height='40px'
            fontSize='12px'
            onClick={() => setStep(2)}
            disabled={!selectedCountry || !amount || !totalAmount || error || isTyping}
            className='button'
        />
    </ContentWrapper>
  )
}

export default PaymentDetails;